// ============================================================
// ByOurConsent.com -- Official Tracker
// /premium/official-tracker
// Premium member tool -- Official Accountability Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const TRACK_TYPES = [
    { id: 'voting_record',  label: 'Voting record on key issues',      desc: 'How they vote on constitutional, fiscal, and civil rights matters' },
    { id: 'statements',     label: 'Public statements vs actions',      desc: 'Compare what they say to how they actually vote' },
    { id: 'donors',         label: 'Top donors and funding sources',    desc: 'Who funds them and what industries have the most influence' },
    { id: 'committees',     label: 'Committee assignments and activity', desc: 'What committees they sit on and whether they show up' },
    { id: 'legislation',    label: 'Bills sponsored and co-sponsored',  desc: 'What legislation they have introduced or supported' },
    { id: 'accountability', label: 'Accountability report card',        desc: 'Overall scorecard on transparency, attendance, and constituent service' },
];

const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', background: i + 1 <= step ? '#8b1a1a' : '#ede8dc', border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600, color: i + 1 <= step ? '#f5f0e8' : '#8a8070', cursor: i + 1 < step ? 'pointer' : 'default' }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />}
            </React.Fragment>
        ))}
    </div>
);

const iStyle = { width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' };
const lStyle = { fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' };
const btnPrimary = { background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' };

const OfficialTrackerPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        officialName: '',
        position:     '',
        state:        '',
        level:        'federal',
        trackTypes:   ['voting_record', 'donors', 'statements', 'accountability'],
        focusIssues:  '',
        context:      ''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));
    const toggleTrack = (id) => setForm(p => ({ ...p, trackTypes: p.trackTypes.includes(id) ? p.trackTypes.filter(t => t !== id) : [...p.trackTypes, id] }));

    const canProceed = form.officialName && form.position && form.trackTypes.length > 0;

    const generate = async () => {
        setLoading(true);
        setError('');

        const tracks = TRACK_TYPES.filter(t => form.trackTypes.includes(t.id)).map(t => t.label).join(', ');
        const levelDesc = form.level === 'federal' ? 'Federal' : `${form.state} State/Local`;

        const prompt = `You are an expert in government transparency, legislative research, and civic accountability. Generate a comprehensive official tracking report and resource guide.

OFFICIAL TO TRACK:
- Name: ${form.officialName}
- Position/Title: ${form.position}
- Level: ${levelDesc}
${form.focusIssues ? `- Focus issues: ${form.focusIssues}` : ''}
${form.context ? `- Context: ${form.context}` : ''}

TRACKING AREAS: ${tracks}

Generate a comprehensive official tracking report:

## OFFICIAL PROFILE
- Full name, current position, and district/jurisdiction
- Time in office and political party
- Key committee assignments (if applicable)
- Contact information: office address, phone, website, social media
- Next election date and whether they are up for re-election

${form.trackTypes.includes('voting_record') ? `
## VOTING RECORD ANALYSIS
Based on available information about ${form.officialName}:
- Overall voting pattern and alignment
- Key votes on constitutional issues (civil liberties, separation of powers, federal overreach)
- Key votes on fiscal issues (spending, debt, budget)
- Notable votes that contradict stated positions
- Voting attendance rate
- How to find their complete voting record:
  * VoteSmart.org search instructions
  * GovTrack.us (federal) search instructions
  * Congress.gov voting records
  * ${form.level === 'state' ? `${form.state} state legislature voting record database` : ''}
${form.focusIssues ? `- Specific voting history on: ${form.focusIssues}` : ''}
` : ''}

${form.trackTypes.includes('statements') ? `
## PUBLIC STATEMENTS VS ACTIONS
- Known public positions and campaign promises
- Where their actions have aligned with their statements
- Notable gaps between rhetoric and voting record
- How to track their statements:
  * Official website and press releases
  * Congressional Record (congress.gov)
  * C-SPAN video archive
  * Local news archives
` : ''}

${form.trackTypes.includes('donors') ? `
## DONOR ANALYSIS
- Top donor industries and amounts (based on available data)
- Largest individual donors or PACs
- Notable donor-to-vote correlations
- How to research their donors:
  * FEC.gov — exact search steps for ${form.officialName}
  * OpenSecrets.org profile page instructions
  * FollowTheMoney.org search instructions
  * What to look for: industry concentration, bundlers, PAC connections
` : ''}

${form.trackTypes.includes('committees') ? `
## COMMITTEE ASSIGNMENTS AND ACTIVITY
- Current committee and subcommittee assignments
- Why these committees matter (what industries/issues they oversee)
- Attendance and participation record
- How to monitor committee activity:
  * committee websites
  * Congress.gov committee pages
  * Hearing schedules and transcripts
` : ''}

${form.trackTypes.includes('legislation') ? `
## LEGISLATION SPONSORED AND CO-SPONSORED
- Notable bills they have introduced
- Bills they have co-sponsored
- What these bills reveal about their priorities
- How to find their legislative record:
  * Congress.gov sponsored legislation search
  * GovTrack.us legislator profile
  * What bills are currently active
` : ''}

${form.trackTypes.includes('accountability') ? `
## ACCOUNTABILITY REPORT CARD
Rate and assess the following (with explanation):
- Transparency: Do they hold town halls, respond to constituents, make records available?
- Attendance: Do they show up to votes and hearings?
- Constituent service: Do they respond to constituent inquiries?
- Ethics: Any ethics complaints, investigations, or conflicts of interest?
- Overall accountability grade: A / B / C / D / F with justification
` : ''}

## HOW TO STAY INFORMED — ONGOING TRACKING
Step-by-step guide to monitor this official going forward:
- Set up Google Alerts for their name
- Subscribe to relevant committee notifications
- Follow on social media with what to watch for
- Track bills at congress.gov or state legislature
- Sign up for watchdog organization newsletters that cover this official

## HOW TO CONTACT AND ENGAGE
- Best ways to contact this official
- How to request a meeting or town hall
- How to submit written comments for the record
- How to testify at committee hearings

## KEY WATCHDOG RESOURCES
- Organizations that monitor this official or their committees
- Journalists who cover this beat
- Relevant FOIA request targets for ongoing monitoring

## ACCOUNTABILITY SUMMARY
3-5 paragraph narrative summary of this official's accountability record, key concerns, and most important things constituents should know and watch.

Be specific — use real database names, URLs, and search instructions. If you have knowledge of this official's record, include it. If not, provide the research framework that will reveal it.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('boc_token')}` },
                body: JSON.stringify({ prompt, max_tokens: 3000 })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');
            setResult({ report: text, officialName: form.officialName, position: form.position });
            setStep(3);
        } catch (err) {
            setError('Failed to generate report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const downloadWord = () => {
        downloadReportAsWord(
            result.report,
            `Official-Tracker-${result.officialName.replace(/[^a-z0-9]/gi,'-').slice(0,30)}`
        );
    };

    const reset = () => { setStep(1); setResult(null); setError(''); setForm({ officialName: '', position: '', state: '', level: 'federal', trackTypes: ['voting_record','donors','statements','accountability'], focusIssues: '', context: '' }); };

    return (
        <>
            <Helmet><title>Official Tracker — By Our Consent</title></Helmet>
            <Nav />

            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>← Premium tools</Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>Official accountability suite</div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>Official Tracker</h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Generate a comprehensive accountability report on any elected official — voting record,
                        donor analysis, statements vs actions, and a complete guide to tracking them going forward.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>
                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — Who do you want to track?
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>
                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Use their full official name and exact title</div>
                                <div style={{ marginBottom: '6px' }}>The more precisely you identify the official, the more accurate the report and database search instructions.</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Senator Jane Marie Williams, U.S. Senator for Virginia, Senate Armed Services Committee"
                                </div>
                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Focus issues — what matters most to you</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "2nd Amendment votes, defense contractor donations, NDAA support, surveillance legislation"
                                </div>
                            </div>
                        </details>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Official's full name *</label>
                            <input style={iStyle} type="text" placeholder="e.g. Jane Marie Williams"
                                value={form.officialName} onChange={e => set('officialName', e.target.value)} />
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Position / title *</label>
                            <input style={iStyle} type="text" placeholder="e.g. U.S. Senator, State Representative, Mayor, City Council Member..."
                                value={form.position} onChange={e => set('position', e.target.value)} />
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Level</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {[['federal','Federal'],['state','State / Local']].map(([val, label]) => (
                                    <button key={val} onClick={() => set('level', val)}
                                        style={{ flex: 1, padding: '8px', background: form.level === val ? '#8b1a1a' : '#f5f0e8', color: form.level === val ? '#f5f0e8' : '#4a5060', border: `0.5px solid ${form.level === val ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', cursor: 'pointer' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {form.level === 'state' && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={lStyle}>State</label>
                                <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                    <option value="">Select state...</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>What do you want to track? *</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {TRACK_TYPES.map(t => (
                                    <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: form.trackTypes.includes(t.id) ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.trackTypes.includes(t.id) ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{t.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{t.desc}</div>
                                        </div>
                                        <input type="checkbox" checked={form.trackTypes.includes(t.id)} onChange={() => toggleTrack(t.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Issues to focus on (optional)</label>
                            <input style={iStyle} type="text"
                                placeholder="e.g. gun rights, healthcare, spending, surveillance, immigration..."
                                value={form.focusIssues} onChange={e => set('focusIssues', e.target.value)} />
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Why are you tracking this official? (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={2}
                                placeholder="Any context about what prompted you to track this official..."
                                value={form.context} onChange={e => set('context', e.target.value)} />
                        </div>

                        <button style={{ ...btnPrimary, opacity: !canProceed ? 0.5 : 1 }} disabled={!canProceed} onClick={() => setStep(2)}>
                            Next: Review & generate →
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — Review your request
                        </div>

                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px', fontFamily: 'sans-serif' }}>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Official:</div>
                                <div style={{ color: '#1e2a3a' }}>{form.officialName}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Position:</div>
                                <div style={{ color: '#1e2a3a' }}>{form.position}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Tracking:</div>
                                <div style={{ color: '#1e2a3a' }}>{TRACK_TYPES.filter(t => form.trackTypes.includes(t.id)).map(t => t.label).join(', ')}</div>
                                {form.focusIssues && <><div style={{ color: '#8a8070', fontWeight: 600 }}>Focus:</div><div style={{ color: '#1e2a3a' }}>{form.focusIssues}</div></>}
                            </div>
                        </div>

                        {error && <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>{error}</div>}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }} disabled={loading} onClick={generate}>
                                {loading ? 'Generating report...' : 'Generate Tracker Report →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                ⏳ Compiling voting record, donor analysis, and accountability resources...
                                <br />This may take 20-30 seconds.
                            </div>
                        )}
                    </div>
                )}

                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>Tracker report ready</div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>{result.officialName} · {result.position}</div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Report</button>
                        </div>

                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.report.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) return <div key={i} style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif', marginTop: i === 0 ? 0 : '1.5rem', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>{line.replace('## ', '')}</div>;
                                if (line.startsWith('- ') || line.startsWith('• ')) return <div key={i} style={{ paddingLeft: '1rem', marginBottom: '4px' }}>• {line.slice(2)}</div>;
                                if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
                                return <div key={i} style={{ marginBottom: '4px' }}>{line}</div>;
                            })}
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Report</button>
                            <button onClick={reset} style={btnSecondary}>Track another official</button>
                        </div>

                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This report is based on AI analysis and publicly available information. Verify all specific claims through official sources before sharing publicly.
                            Information may not reflect the most recent votes or disclosures.
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

export default OfficialTrackerPage;
